/**
 * PropertyDiffValue - Coordinated old → new value renderer for timeline diffs.
 *
 * Three layouts, picked by value type and length:
 *
 * 1. **No prior value** — render the new value plainly. No diff UI.
 *
 * 2. **Text-like values** (text, number, anything that flows through
 *    `TextValueLabel`) use char-based truncation:
 *    - Inline `[old] → [new]` for short values.
 *    - Stacked `[old] / ↘ [new]` with a coordinated "Show more / Show less"
 *      toggle for long values. Truncation is handled by `PropertyValueLabel`'s
 *      `maxLength` prop, which reliably honors the limit for these types.
 *
 * 3. **Non-text values** (markdown, multi-value chips, currency, select,
 *    etc.) use DOM-measured overflow detection:
 *    - Inline `→` layout, wrapped in `CollapsibleContent`.
 *    - The reusable container measures `scrollHeight` against a height cap
 *      and only shows the toggle when the rendered content actually
 *      overflows. This avoids the previous bug where char-count heuristics
 *      promised expandable content for value types whose specialized
 *      renderers silently ignored `maxLength` (e.g. markdown descriptions,
 *      `+N` chip badges for multi-value arrays).
 */

import { CornerDownRight } from '@untitledui/icons';
import type { MouseEvent } from 'react';
import { memo, useCallback, useMemo, useState } from 'react';

import { CollapsibleContent } from '@/lib/base/collapsible-content';
import { PropertyValueLabel } from '@/lib/crm/components/properties/labels/PropertyValueLabel';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import { unwrapPropertyValue } from '@/types/crm';

/**
 * Characters at which a single text-like value is considered "long" enough
 * that stacking the old/new pair with a hook icon is clearer than an inline
 * arrow. The same limit caps the collapsed render length inside the stacked
 * view. Only used for value types that PropertyValueLabel actually truncates
 * via `maxLength`; for everything else, overflow is measured in the DOM.
 */
export const PROPERTY_DIFF_LONG_THRESHOLD = 150;

/**
 * Visual height cap for the DOM-measured non-text diff in pixels. Roughly
 * fits a ~5-line preview of the old value + ~5-line preview of the new
 * value before the fade kicks in.
 */
const NON_TEXT_DIFF_MAX_HEIGHT_PX = 144;

/**
 * Field types in `PropertyValueLabel` whose specialized renderers silently
 * ignore `maxLength` — meaning char-based truncation has no visible effect
 * for these. We route them through DOM-measured overflow detection instead.
 *
 * Kept in sync with the switch statement in `PropertyValueLabel`. `number`
 * is intentionally NOT here because its renderer DOES respect `maxLength`.
 */
const SPECIALIZED_FIELD_TYPES: ReadonlySet<string> = new Set([
  'phone_number',
  'currency',
  'multi_currency',
  'select',
  'timezone',
  'date',
  'datetime',
  'duration_days',
  'file_size',
  'url',
  'markdown',
  'manifest',
  'working_hours_schedule',
  'location_type',
  'color',
  'permission_group',
  'domain',
]);

/**
 * Whether `PropertyValueLabel`'s `maxLength` prop has a visible effect for
 * the given schema. Multi-value renders as `first value + "+N" badge` and
 * the badge is unaffected by `maxLength`, so multi is always non-text.
 */
function supportsCharBasedTruncation(schema?: PropertyTypeSchema): boolean {
  if (schema?.is_multi) {
    return false;
  }
  const fieldType = schema?.field_type_name;
  if (!fieldType) {
    // No schema → falls through to default case in PropertyValueLabel,
    // which renders via TextValueLabel and honors maxLength.
    return true;
  }
  if (fieldType === 'number') {
    return true;
  }
  return !SPECIALIZED_FIELD_TYPES.has(fieldType);
}

interface PropertyDiffValueProps {
  oldValue: unknown;
  newValue: unknown;
  schema?: PropertyTypeSchema;
  orgId?: number;
  /**
   * The entity's raw `properties` map, forwarded to PropertyValueLabel so a
   * `formula` field can resolve its target entity type from a sibling
   * property.
   */
  entityProperties?: Record<string, unknown>;
}

function getDisplayLength(value: unknown): number {
  if (value === null || value === undefined) {
    return 0;
  }
  // timeline event payload: single-level {value} wrapper — unwrap once via unwrapPropertyValue, then recurse for formatting
  if (typeof value === 'object') {
    const unwrapped = unwrapPropertyValue(value);
    if (unwrapped !== value) {
      return getDisplayLength(unwrapped);
    }
  }
  if (typeof value === 'object') {
    return JSON.stringify(value).length;
  }
  return String(value).length;
}

export const PropertyDiffValue = memo(function PropertyDiffValue({
  oldValue,
  newValue,
  schema,
  orgId,
  entityProperties,
}: PropertyDiffValueProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggle = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setIsExpanded(prev => !prev);
  }, []);

  const hasOldValue = oldValue !== undefined;
  const charBased = supportsCharBasedTruncation(schema);

  // Char-based metrics — only relevant for the text/number path.
  const oldLen = useMemo(
    () => (charBased ? getDisplayLength(oldValue) : 0),
    [charBased, oldValue]
  );
  const newLen = useMemo(
    () => (charBased ? getDisplayLength(newValue) : 0),
    [charBased, newValue]
  );
  const isLongCharBased =
    oldLen > PROPERTY_DIFF_LONG_THRESHOLD ||
    newLen > PROPERTY_DIFF_LONG_THRESHOLD;

  const effectiveMaxLength =
    charBased && !isExpanded ? PROPERTY_DIFF_LONG_THRESHOLD : undefined;

  // First-time sets (no prior value) have no coordinated diff toggle, so long
  // text used to render in full — e.g. an entire call transcript dumped into
  // the timeline row. Cap them at the same threshold and let
  // PropertyValueLabel's own inline Show more/less toggle take over
  // (hideExpandButton is false on this path).
  const noPriorValueMaxLength = charBased
    ? PROPERTY_DIFF_LONG_THRESHOLD
    : undefined;

  const newValueNode = useMemo(
    () => (
      <PropertyValueLabel
        value={newValue}
        orgId={orgId}
        propertyType={schema}
        fontSize='sm'
        className='font-medium'
        maxLength={hasOldValue ? effectiveMaxLength : noPriorValueMaxLength}
        hideExpandButton={hasOldValue}
        entityProperties={entityProperties}
      />
    ),
    [
      newValue,
      orgId,
      schema,
      hasOldValue,
      effectiveMaxLength,
      noPriorValueMaxLength,
      entityProperties,
    ]
  );

  const oldValueNode = useMemo(
    () => (
      <PropertyValueLabel
        value={oldValue}
        orgId={orgId}
        propertyType={schema}
        fontSize='sm'
        className='text-tertiary line-through'
        maxLength={effectiveMaxLength}
        hideExpandButton
        entityProperties={entityProperties}
      />
    ),
    [oldValue, orgId, schema, effectiveMaxLength, entityProperties]
  );

  // No prior value → render just the new value. No diff UI needed.
  if (!hasOldValue) {
    return newValueNode;
  }

  // ---------------------------------------------------------------------
  // Non-text path: same inline `old → new` layout as text-short, wrapped
  // in CollapsibleContent. The reusable container measures actual
  // rendered height and only shows the toggle when content truly
  // overflows the cap. This means:
  //   - Short non-text diffs (currency, date, select, single multi-value
  //     chip) stay compact on one line with the `→` arrow.
  //   - Long non-text diffs (markdown body, expanded multi-value list)
  //     wrap, exceed the cap, and get a working toggle — fixing the
  //     previous bug where a button appeared but did nothing because the
  //     specialized renderer ignored `maxLength`.
  // ---------------------------------------------------------------------
  if (!charBased) {
    return (
      <CollapsibleContent maxCollapsedHeight={NON_TEXT_DIFF_MAX_HEIGHT_PX}>
        <span className='inline-flex flex-wrap items-center gap-1 max-w-full'>
          {oldValueNode}
          <span className='text-tertiary' aria-hidden='true'>
            →
          </span>
          {newValueNode}
        </span>
      </CollapsibleContent>
    );
  }

  // ---------------------------------------------------------------------
  // Char-based text/number path: inline for short, stacked + char toggle
  // for long. PropertyValueLabel.maxLength does the actual truncation.
  // ---------------------------------------------------------------------
  if (!isLongCharBased) {
    return (
      <span className='inline-flex flex-wrap items-center gap-1 max-w-full'>
        {oldValueNode}
        <span className='text-tertiary' aria-hidden='true'>
          →
        </span>
        {newValueNode}
      </span>
    );
  }

  return (
    <span className='mt-1 flex flex-col gap-1'>
      <span className='block break-words'>{oldValueNode}</span>
      <span className='flex items-start gap-2'>
        <CornerDownRight
          aria-label='changed to'
          className='shrink-0 w-4 h-4 mt-0.5 text-tertiary'
        />
        <span className='flex-1 min-w-0 break-words'>{newValueNode}</span>
      </span>
      <button
        type='button'
        onClick={handleToggle}
        className='self-start ml-6 text-xs font-medium text-brand-secondary hover:text-brand-secondary_hover hover:underline underline-offset-2 cursor-pointer'
        aria-expanded={isExpanded}
      >
        {isExpanded ? 'Show less' : 'Show more'}
      </button>
    </span>
  );
});
